import { QueryParams, axiosInstance } from './req';
import { DeviceInfo, SharedDeviceInfo } from '@/types/device-info';

export const loadDevices = async (
    userId: string,
    { page, perPage, filter }: QueryParams,
    deviceType: string,
    name: string,
): Promise<DeviceInfo[]> => {
    let url = `/api/devices/find/${userId}`;
    if (deviceType && deviceType != 'all') {
        url = `${url}?type=${deviceType}${name ? `&name=${name}` : ''}`;
    } else if (name) {
        url = `${url}?name=${name}`;
    }
    return axiosInstance.get(url, { params: { page, perPage, filter } }).then(({ data }) => data);
};

export const loadDevice = async (deviceUUID: string): Promise<DeviceInfo> => {
    return axiosInstance.get(`/api/devices/${deviceUUID}`).then(({ data }) => {
        if (data.device?.length > 0) {
            data.device[0].shares = data.sharedDevices;
        }
        return data.device?.length > 0 ? data.device[0] : null;
    });
};

export const updateDevice = async (device: Partial<DeviceInfo>): Promise<DeviceInfo> => {
    return axiosInstance.put(`/api/devices/${device._id}`, device).then(({ data }) => data);
};

export const loadShare = async (deviceUuid: string): Promise<SharedDeviceInfo[]> => {
    return axiosInstance.get(`/api/share-device/find/device/${deviceUuid}`).then(({ data }) => data);
};

export const deleteShare = async (shareId: string): Promise<DeviceInfo> => {
    return axiosInstance.delete(`/api/share-device/${shareId}`).then(({ data }) => data);
};

export const createShare = async (share: SharedDeviceInfo): Promise<SharedDeviceInfo> => {
    return axiosInstance.post(`/api/share-device/insert`, share).then(({ data }) => data);
};

export const updateShare = async (share: SharedDeviceInfo): Promise<SharedDeviceInfo> => {
    return axiosInstance.put(`/api/share-device/${share._id}`, share).then(({ data }) => data);
};

export const resendShare = async (share: SharedDeviceInfo): Promise<SharedDeviceInfo> => {
    const { userId, sharedUserWithEmail, _id, deviceUUID } = share;
    return axiosInstance
        .post(`/api/share-device/resendmessage`, { userId, sharedUserWithEmail, deviceUUID, shareId: _id })
        .then(({ data }) => data);
};

export const removeAllShareOfDevice = async (deviceUuid: string): Promise<SharedDeviceInfo> => {
    return axiosInstance.delete(`/api/share-device/all/${deviceUuid}`).then(({ data }) => data);
};

export const removeDevice = async (deviceId: string): Promise<DeviceInfo> => {
    return axiosInstance.delete(`/api/devices/${deviceId}`).then(({ data }) => data);
};

export const locateDevice = async (deviceUUID: string, locationReportMode: string) => {
    return axiosInstance.post(`/api/devices/locate/${deviceUUID}`, { locationReportMode }).then(({ data }) => data);
};

export const updateDeviceAlarm = async (deviceUUID: string, value: string) => {
    return axiosInstance.put(`/api/devices/alarm/${deviceUUID}/${value}`).then(({ data }) => data);
};

export const updateGeoFence = async (deviceUUID: string, value: string) => {
    return axiosInstance.put(`/api/devices/geo-fence/${deviceUUID}/${value}`).then(({ data }) => data);
};

export const createGeoFenceRoles = async (deviceUUID: string, trackingTime = '', liveTrackingUnable = false) => {
    return axiosInstance.post(`/api/devices/geo-fence/rules/${deviceUUID}`, { trackingTime, liveTrackingUnable }).then(({ data }) => data);
};

export const deleteGeoFence = async (deviceUUID: string) => {
    return axiosInstance.delete(`/api/devices/geo-fence/${deviceUUID}`).then(({ data }) => data);
};

export const stopLiveTracking = async (deviceUUID: string) => {
    return axiosInstance.post(`/api/devices/locate/stopTracking/${deviceUUID}`).then(({ data }) => data);
};

export const addNotifyEmail = async (deviceUUID: string, email: string) => {
    return axiosInstance.post(`/api/devices/notification-emails/${deviceUUID}`, { email }).then(({ data }) => data);
};

export const removeNotifyEmail = async (deviceUUID: string, email: string) => {
    return axiosInstance.delete(`/api/devices/notification-emails/${deviceUUID}/${email}`).then(({ data }) => data);
};

export const getModemInfo = async (deviceUUID: string) => {
    return axiosInstance.get(`/api/devices/modemInfo/${deviceUUID}`).then(({ data }) => data);
};

export const updateOperatingMode = async (id: string, operatingMode: string) => {
    return axiosInstance.put(`/api/devices/operating-mode/${id}`, { operatingMode }).then(({ data }) => data);
};

export const updateAirplaneMode = async (deviceUUID: string, duration: number) => {
    return axiosInstance.put(`/api/devices/airplane-mode/${deviceUUID}`, { duration }).then(({ data }) => data);
};

export const updateDeviceAlerts = async (deviceId: string, pushNotification: boolean, emailAlerts: boolean) => {
    return axiosInstance
        .put(`/api/devices/alert-settings/${deviceId}`, { pushNotification, emailAlerts })
        .then(({ data }) => data);
};

export const transferDevice = async (deviceId: string, userId: string) => {
    return axiosInstance.post(`/api/devices/transfer`, { deviceId, userId }).then(({ data }) => data);
}
